import { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import {
  channelManagerList,
  channelManagerInsert,
  channelManagerUpdate,
} from "@repo/api-client";
import type {
  ChannelListItem,
  ChannelInsert,
  ChannelUpdate,
} from "@repo/types";

export const useChannelManager = () => {
  const [channels, setChannels] = useState<ChannelListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [selectedChannel, setSelectedChannel] =
    useState<ChannelListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  const [insertForm, setInsertForm] = useState<Partial<ChannelInsert>>({
    IsActive: 1,
    Priority: 0,
    IsCredentialsEncrypted: 0,
    SupportedFeatures: [],
  });

  const [updateForm, setUpdateForm] = useState<Partial<ChannelUpdate>>({});

  const fetchChannels = useCallback(
    async (
      currentPage: number,
      currentSearch: string,
      currentStatus: string,
    ) => {
      try {
        setLoading(true);
        const response = await channelManagerList({
          PageNumber: currentPage,
          PageSize: pageSize,
          Search: currentSearch || null,
          IsActive:
            currentStatus === "all" ? null : currentStatus === "active" ? 1 : 0,
        });

        if (response.Success) {
          setChannels((response.Data || []).slice(0, pageSize));
          setTotalCount(
            (response as any).TotalCount || response.Data?.length || 0,
          );
        } else {
          toast.error(response.Message || "Failed to fetch channels");
        }
      } catch (error) {
        console.error("Error fetching channels:", error);
        toast.error("An unexpected error occurred while fetching channels");
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  // Debounce search/status changes and reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage((prev) => {
        if (prev === 1) {
          fetchChannels(1, searchQuery, status);
          return 1;
        }
        return 1; // triggers the page useEffect below
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, status, fetchChannels]);

  // Fetch when page changes
  useEffect(() => {
    fetchChannels(page, searchQuery, status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleOpenInsertModal = useCallback(() => {
    setIsEditMode(false);
    setInsertForm({
      IsActive: 1,
      Priority: 0,
      IsCredentialsEncrypted: 0,
    });
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((channel: ChannelListItem) => {
    setSelectedChannel(channel);
    setIsEditMode(true);
    setUpdateForm({
      ChannelID: channel.ChannelID,
      ChannelCode: channel.ChannelCode,
      ChannelName: channel.ChannelName,
      ChannelType: channel.ChannelType,
      APIBaseURL: channel.APIBaseURL,
      APIVersion: channel.APIVersion,
      Protocol: channel.Protocol,
      AuthType: channel.AuthType,
      IsActive: channel.IsActive ? 1 : 0,
      Priority: channel.Priority,
      IsCredentialsEncrypted: channel.IsCredentialsEncrypted ? 1 : 0,
      CommissionPercentage: channel.CommissionPercentage,
      CommissionType: channel.CommissionType,
      CurrencyCode: channel.CurrencyCode,
      SupportedFeatures: channel.SupportedFeatures || [],
      LogoURL: channel.LogoURL,
      SupportEmail: channel.SupportEmail,
      DocumentationURL: channel.DocumentationURL,
      Notes: channel.Notes,
      ModifiedBy: null,
    });
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((channel: ChannelListItem) => {
    setSelectedChannel(channel);
    setIsViewModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedChannel(null);
  }, []);

  const handleNextPage = useCallback(() => {
    if (channels.length === pageSize) {
      setPage((p) => p + 1);
    }
  }, [channels.length, pageSize]);

  const handlePrevPage = useCallback(() => {
    if (page > 1) {
      setPage((p) => p - 1);
    }
  }, [page]);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      let response;
      if (isEditMode) {
        response = await channelManagerUpdate(updateForm);
      } else {
        response = await channelManagerInsert(insertForm);
      }

      if (response.Success) {
        toast.success(
          isEditMode
            ? "Channel updated successfully"
            : "Channel registered successfully",
        );
        setIsModalOpen(false);
        fetchChannels(page, searchQuery, status);
      } else {
        toast.error(response.Message || "Failed to save channel");
      }
    } catch (error) {
      console.error("Error saving channel:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSaving(false);
    }
  }, [
    isEditMode,
    updateForm,
    insertForm,
    fetchChannels,
    page,
    searchQuery,
    status,
  ]);

  return {
    channels,
    loading,
    searchQuery,
    setSearchQuery,
    status,
    setStatus,
    selectedChannel,
    page,
    pageSize,
    totalCount,
    isModalOpen,
    isViewModalOpen,
    isEditMode,
    isSaving,
    insertForm,
    setInsertForm,
    updateForm,
    setUpdateForm,
    handleOpenInsertModal,
    handleEdit,
    handleView,
    handleSave,
    handleCloseModal,
    handleNextPage,
    handlePrevPage,
  };
};
